import { AfterViewInit, Component, ElementRef, OnDestroy, input, output, signal, viewChild } from '@angular/core';

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Reusable modal shell (overlay + centered card) for "add" flows that used to live inline on
 * the page (see employees-page's invite form). Closes on overlay click, the × button, or Escape.
 * Callers own the body/footer content via content projection; this only owns the chrome.
 *
 * Keyboard and focus: the first field gets the focus on open, Tab stays inside the dialog and
 * the focus returns to the opener on close. Two guards against losing work by accident:
 *  - `busy`: a request is in flight, closing is blocked (the buttons of the caller should be
 *    disabled too, so nothing looks clickable that does nothing).
 *  - `confirmClose`: text of a question asked before closing ("Änderungen verwerfen?"). Pass
 *    it only while there is something to lose (`form.dirty`); null closes right away.
 */
@Component({
  selector: 'app-modal',
  host: {
    '(document:keydown.escape)': 'onEscape($event)',
  },
  template: `
    <div class="modal-overlay" (mousedown)="onOverlayDown($event)" (click)="onOverlayClick($event)">
      <div
        #card
        class="modal-card card card-elevated"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        [attr.aria-label]="title()"
        [attr.aria-busy]="busy() || null"
        (keydown.tab)="trapFocus($event)"
        (keydown.shift.tab)="trapFocus($event)"
      >
        <div class="modal-header">
          <h3>{{ title() }}</h3>
          <button type="button" class="modal-close" (click)="close()" aria-label="Schliessen" [disabled]="busy()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <ng-content></ng-content>
        </div>
        @if (confirming()) {
          <div class="modal-confirm" role="alertdialog" aria-label="Schliessen bestätigen">
            <span>{{ confirmClose() }}</span>
            <div class="modal-confirm-actions">
              <button #keepBtn type="button" class="btn-secondary btn-sm" (click)="keepOpen()">Weiter bearbeiten</button>
              <button type="button" class="btn-danger btn-sm" (click)="discard()">Verwerfen</button>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 21, 32, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--s-6);
      z-index: 1000;
      animation: modal-overlay-in var(--t-base);
    }

    .modal-card {
      width: 100%;
      max-width: 640px;
      max-height: calc(100vh - var(--s-12));
      display: flex;
      flex-direction: column;
      animation: modal-card-in var(--t-base);
      outline: none;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--s-4);
      margin-bottom: var(--s-4);
      flex-shrink: 0;

      h3 {
        font-weight: var(--font-weight-heading);
      }
    }

    .modal-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: var(--r-full);
      color: var(--color-text-secondary);
      flex-shrink: 0;
      transition: background-color var(--t-fast), color var(--t-fast);

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover:not(:disabled) {
        background: var(--color-primary-lighter);
        color: var(--color-primary);
      }

      &:disabled {
        opacity: .4;
        cursor: not-allowed;
      }
    }

    .modal-body {
      overflow-y: auto;
    }

    .modal-confirm {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--s-3);
      margin-top: var(--s-4);
      padding: var(--s-3) var(--s-4);
      border-radius: var(--r-md);
      background: var(--color-warning-bg);
      color: var(--color-warning-text);
      font-size: 14px;
      flex-shrink: 0;
    }

    .modal-confirm-actions {
      display: flex;
      gap: var(--s-2);
    }

    @keyframes modal-overlay-in {
      from {
        opacity: 0;
      }
    }

    @keyframes modal-card-in {
      from {
        opacity: 0;
        transform: translateY(8px) scale(0.98);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .modal-overlay,
      .modal-card {
        animation: none;
      }
    }
  `,
})
export class Modal implements AfterViewInit, OnDestroy {
  readonly title = input.required<string>();
  /** A request is in flight: closing is blocked until it finished. */
  readonly busy = input(false);
  /** Question asked before closing; null = close without asking. */
  readonly confirmClose = input<string | null>(null);
  readonly closed = output<void>();

  protected readonly confirming = signal(false);

  private readonly card = viewChild.required<ElementRef<HTMLElement>>('card');
  private readonly keepBtn = viewChild<ElementRef<HTMLButtonElement>>('keepBtn');
  private opener: HTMLElement | null = null;
  private overlayPressed = false;

  ngAfterViewInit(): void {
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const card = this.card().nativeElement;
    // First field of the body (not the × in the header); the card itself when there is none.
    const first = card.querySelector<HTMLElement>(`.modal-body :is(${FOCUSABLE})`);
    (first ?? card).focus();
  }

  ngOnDestroy(): void {
    if (this.opener?.isConnected) {this.opener.focus();}
  }

  protected onEscape(event: Event): void {
    // A date picker or select popup uses Escape for itself; only a plain Escape closes.
    if (event.defaultPrevented) {return;}
    if (this.confirming()) { this.keepOpen(); return; }
    this.close();
  }

  // Close on a real click on the backdrop only: selecting text inside a field and releasing the
  // mouse outside the card also fires `click` on the overlay and used to throw the form away.
  protected onOverlayDown(event: MouseEvent): void {
    this.overlayPressed = event.target === event.currentTarget;
  }

  protected onOverlayClick(event: MouseEvent): void {
    if (this.overlayPressed && event.target === event.currentTarget) {this.close();}
    this.overlayPressed = false;
  }

  protected trapFocus(event: Event): void {
    const items = [...this.card().nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((el) => el.offsetParent !== null);
    if (items.length === 0) { event.preventDefault(); return; }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const shift = (event as KeyboardEvent).shiftKey;
    if (shift && (active === first || active === this.card().nativeElement)) {
      event.preventDefault();
      last.focus();
    } else if (!shift && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  protected close(): void {
    if (this.busy()) {return;}
    if (this.confirmClose() && !this.confirming()) {
      this.confirming.set(true);
      queueMicrotask(() => this.keepBtn()?.nativeElement.focus());
      return;
    }
    this.closed.emit();
  }

  protected keepOpen(): void {
    this.confirming.set(false);
    const card = this.card().nativeElement;
    (card.querySelector<HTMLElement>(`.modal-body :is(${FOCUSABLE})`) ?? card).focus();
  }

  protected discard(): void {
    this.confirming.set(false);
    this.closed.emit();
  }
}
